import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Determine API base URL based on environment
const getApiBaseUrl = () => {
  // Get environment URL
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  // Debug logging
  console.log('[Auth] Environment REACT_APP_BACKEND_URL:', envUrl);
  console.log('[Auth] Window location:', typeof window !== 'undefined' ? window.location.href : 'N/A');
  
  // For production deployments, ALWAYS use relative /api path
  // This works because the Emergent platform routes /api/* to the backend
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isPreview = hostname.includes('preview.emergentagent.com');
    
    console.log('[Auth] Hostname:', hostname, '| isLocalhost:', isLocalhost, '| isPreview:', isPreview);
    
    // For production domains (not localhost, not preview), use relative path
    if (!isLocalhost && !isPreview) {
      console.log('[Auth] Using relative /api for production domain');
      return '/api';
    }
    
    // For preview environment, use the explicit URL
    if (isPreview && envUrl && envUrl.trim()) {
      console.log('[Auth] Using envUrl for preview:', envUrl);
      return `${envUrl}/api`;
    }
  }
  
  // For local development with explicit URL
  if (envUrl && envUrl.trim()) {
    console.log('[Auth] Using envUrl:', envUrl);
    return `${envUrl}/api`;
  }
  
  // Fallback for local development
  console.log('[Auth] Using localhost fallback');
  return 'http://localhost:8001/api';
};

const API = getApiBaseUrl();
console.log('[Auth] API Base URL:', API);

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage cache for instant display
  const [user, setUser] = useState(() => {
    try {
      const cachedUser = localStorage.getItem('user');
      return cachedUser ? JSON.parse(cachedUser) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [validatedTolobaAccess, setValidatedTolobaAccess] = useState([]);
  const validationDone = useRef(false);

  // Memoize api instance to prevent recreating on every render
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    
    instance.interceptors.response.use(
      response => response,
      error => {
        // Only logout on actual 401 Unauthorized - not on server errors or network issues
        if (error.response?.status === 401 && error.response?.data?.detail !== 'User not found') {
          // Check if it's a genuine token expiry/invalid token
          const detail = error.response?.data?.detail || '';
          if (detail.includes('Token expired') || detail.includes('Invalid token') || detail === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
    
    return instance;
  }, [token]);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only logout on specific 401 errors - not on network/server errors
      if (error.response?.status === 401) {
        const detail = error.response?.data?.detail || '';
        // Only logout if token is genuinely expired/invalid
        if (detail.includes('Token expired') || detail.includes('Invalid token')) {
          console.log('Token expired/invalid, logging out');
          logout();
        } else {
          // Other 401 errors (like user not found) - still logout
          console.log('Auth error:', detail);
          logout();
        }
      } else if (error.response?.status >= 500 || !error.response) {
        // Server error or network error - keep user logged in with cached data
        console.warn('Server/network error, keeping session alive');
        // Try to restore user from localStorage if available
        const cachedUser = localStorage.getItem('user');
        if (cachedUser) {
          try {
            setUser(JSON.parse(cachedUser));
          } catch (e) {
            console.error('Failed to parse cached user');
          }
        }
      }
    } finally {
      setLoading(false);
    }
  }, [token, api]);

  // Validate toloba access against actual campaigns - run once when user loads
  useEffect(() => {
    const validateAccess = async () => {
      const userAccess = user?.toloba_access || [];
      if (userAccess.length === 0 || !token || validationDone.current) {
        if (userAccess.length === 0) setValidatedTolobaAccess([]);
        return;
      }
      
      try {
        validationDone.current = true;
        // Use the api instance with proper error handling
        const response = await api.get('/toloba-campaigns?status=active');
        const activeCampaigns = response.data.campaigns || [];
        const activeCampaignIds = activeCampaigns.map(c => c.id);
        
        // Filter out access to deleted campaigns
        const validAccess = userAccess.filter(access => 
          activeCampaignIds.includes(access.campaign_id)
        );
        setValidatedTolobaAccess(validAccess);
      } catch (error) {
        // If fetch fails, use stored access - don't logout
        console.warn('Failed to validate toloba access:', error.message);
        setValidatedTolobaAccess(userAccess);
      }
    };
    
    if (user && !loading) {
      validateAccess();
    }
  }, [user, token, loading, api]);

  // Reset validation when user changes
  useEffect(() => {
    validationDone.current = false;
  }, [user?.id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));  // Cache user data
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const loginWithITS = async (itsId, password) => {
    const response = await api.post('/auth/login-its', { its_id: itsId, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));  // Cache user data
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, fullName, role = 'admin') => {
    const response = await api.post('/auth/register', {
      email,
      password,
      full_name: fullName,
      role
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));  // Cache user data
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');  // Clear cached user
    setToken(null);
    setUser(null);
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || 
                   (user?.roles && (user.roles.includes('admin') || user.roles.includes('super_admin')));
  const isSuperAdmin = user?.role === 'super_admin' || (user?.roles && user.roles.includes('super_admin'));
  const isLeadership = user?.leadership_role && ['masool', 'masoola', 'musaid', 'musaida'].includes(user.leadership_role);
  const userMohalla = user?.mohalla;
  const userSector = user?.sector;
  const userSectors = user?.sectors || (user?.sector ? [user.sector] : []); // Support multiple sectors
  
  // New role-based permission helpers - support multiple roles
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const isMasool = userRoles.includes('masool') || user?.leadership_role === 'masool' || user?.leadership_role === 'masoola';
  const isMusaid = user?.leadership_role === 'musaid' || user?.leadership_role === 'musaida';
  const isFMBViewer = userRoles.includes('fmb_viewer');
  const isKhidmatGuzar = userRoles.includes('khidmat_guzar') || (user?.role === 'mumineen' && user?.event_access?.length > 0);
  const isTolobaCollector = userRoles.includes('toloba_collector');
  const isTolobaKG = validatedTolobaAccess.length > 0; // User assigned to active Toloba campaigns
  const isListManager = userRoles.includes('list_manager');
  const isTokenManager = userRoles.includes('token_manager');
  const isWajebaat = userRoles.includes('wajebaat');
  const isSeatManagement = userRoles.includes('seat_management');
  const isZabhiyat = userRoles.includes('zabhiyat');
  const isEventIncharge = userRoles.includes('event_incharge');
  const isFormsManager = userRoles.includes('forms_manager');
  
  // Permission checks - use server-provided values or calculate locally
  const canExportExcel = user?.can_export_excel ?? isSuperAdmin; // Server provides this, fallback to super admin check
  const canExportPDF = user?.can_export_pdf ?? (isAdmin || isMasool || isMusaid || isTokenManager || isWajebaat); // Server provides this
  const canViewDashboard = isSuperAdmin || user?.role === 'admin' || user?.role === 'khidmat_guzar'; // No dashboard for Masool/Musaid/FMB Viewer
  const canViewAuditLogs = isSuperAdmin; // ONLY Super Admin can view audit logs
  const canEditData = isSuperAdmin || user?.role === 'admin'; // Only admins can edit
  
  // Accessible tabs from server
  const accessibleTabs = user?.accessible_tabs || [];
  
  // Custom permissions for toloba_collector and leadership roles
  const canViewFMB = isFMBViewer || isAdmin || isSuperAdmin || (!isTolobaCollector && !isMasool && !isMusaid);
  const canViewSectorList = isAdmin || isSuperAdmin || isTolobaCollector;
  const canExportSectorPDF = isAdmin || isSuperAdmin || isTolobaCollector;
  const canAccessToloba = isAdmin || isSuperAdmin || isTolobaCollector || isKhidmatGuzar || isMasool || isMusaid || isTolobaKG;
  
  // Scope restrictions - support multiple sectors for musaid
  const restrictedMohalla = (isMasool || isMusaid) ? userMohalla : null; // Masool/Masoola restricted to their Mohalla
  const restrictedSector = isMusaid ? userSector : null; // Single sector for backward compatibility
  const restrictedSectors = isMusaid ? userSectors : null; // Multiple sectors for musaid
  
  // Toloba access info - use validated access that checks campaigns still exist
  const tolobaAccess = validatedTolobaAccess;
  const eventAccess = user?.event_access || [];
  const formAccess = user?.form_access || [];

  return (
    <AuthContext.Provider value={{ 
      user, token, loading, login, loginWithITS, register, logout, api, 
      isAdmin, isSuperAdmin, isLeadership, userMohalla, userSector, userSectors,
      isMasool, isMusaid, isFMBViewer, isKhidmatGuzar, isTolobaCollector, isTolobaKG, isListManager,
      isTokenManager, isWajebaat, isSeatManagement, isZabhiyat, isZabhiyatRole: isZabhiyat, isEventIncharge, isFormsManager,
      userRoles,
      canExportExcel, canExportPDF, canViewDashboard, canViewAuditLogs, canEditData,
      canViewFMB, canViewSectorList, canExportSectorPDF, canAccessToloba,
      restrictedMohalla, restrictedSector, restrictedSectors,
      accessibleTabs,
      tolobaAccess, eventAccess, formAccess
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
